import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Bell,
  AlertTriangle,
  Clock,
  XCircle,
  Package,
  CheckCircle,
  Trash2,
  CheckCheck,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await api.get("/notifications");
      setNotifications(response.data);
    } catch (error) {
      toast.error("Erro ao carregar notificações");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      setNotifications(
        notifications.map((n) =>
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );
    } catch (error) {
      toast.error("Erro ao marcar como lida");
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.put("/notifications/read-all");
      setNotifications(notifications.map((n) => ({ ...n, is_read: true })));
      toast.success("Todas as notificações marcadas como lidas");
    } catch (error) {
      toast.error("Erro ao marcar notificações");
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      setNotifications(notifications.filter((n) => n.id !== notificationId));
      toast.success("Notificação excluída");
    } catch (error) {
      toast.error("Erro ao excluir notificação");
    }
  };

  const formatDateTime = (dateStr) => {
    try {
      return format(new Date(dateStr), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "low_stock":
        return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      case "expiring":
        return <Clock className="h-5 w-5 text-orange-500" />;
      case "expired":
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Bell className="h-5 w-5 text-blue-500" />;
    }
  };

  const getNotificationBadge = (type) => {
    switch (type) {
      case "low_stock":
        return <Badge className="alert-badge-warning">Estoque Baixo</Badge>;
      case "expiring":
        return <Badge className="bg-orange-100 text-orange-800">A Vencer</Badge>;
      case "expired":
        return <Badge className="alert-badge-danger">Vencido</Badge>;
      default:
        return <Badge className="alert-badge-info">Info</Badge>;
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notifications-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
            Notificações
          </h1>
          <p className="text-slate-500 mt-1">
            Alertas de estoque baixo e produtos a vencer
          </p>
        </div>
        {unreadCount > 0 && (
          <Button
            variant="outline"
            onClick={handleMarkAllAsRead}
            className="gap-2"
            data-testid="mark-all-read-btn"
          >
            <CheckCheck className="h-4 w-4" />
            Marcar todas como lidas
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {notifications.filter((n) => n.type === "low_stock").length}
                </p>
                <p className="text-sm text-slate-500">Estoque Baixo</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center">
                <Clock className="h-6 w-6 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {notifications.filter((n) => n.type === "expiring").length}
                </p>
                <p className="text-sm text-slate-500">A Vencer</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center">
                <XCircle className="h-6 w-6 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {notifications.filter((n) => n.type === "expired").length}
                </p>
                <p className="text-sm text-slate-500">Vencidos</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Notifications List */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="font-heading text-lg">
            Todas as Notificações
          </CardTitle>
          <CardDescription>
            {unreadCount > 0
              ? `${unreadCount} não lida(s)`
              : "Todas as notificações foram lidas"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <CheckCircle className="h-12 w-12 mb-4 text-emerald-300" />
              <p className="font-medium">Nenhuma notificação</p>
              <p className="text-sm">Seu estoque está em dia!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`flex items-start gap-4 p-4 rounded-lg border transition-colors ${
                    notification.is_read
                      ? "bg-slate-50 border-slate-100"
                      : "bg-white border-slate-200 shadow-sm"
                  }`}
                  data-testid={`notification-${notification.id}`}
                >
                  <div className="mt-1">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {getNotificationBadge(notification.type)}
                      {!notification.is_read && (
                        <span className="w-2 h-2 bg-primary rounded-full" />
                      )}
                    </div>
                    <p className="font-medium text-slate-900">
                      {notification.title}
                    </p>
                    <p className="text-sm text-slate-600 mt-1">
                      {notification.message}
                    </p>
                    {notification.product_name && (
                      <div className="flex items-center gap-2 mt-2">
                        <Package className="h-4 w-4 text-slate-400" />
                        <span className="text-sm text-slate-500">
                          {notification.product_name}
                        </span>
                      </div>
                    )}
                    <p className="text-xs text-slate-400 mt-2">
                      {formatDateTime(notification.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!notification.is_read && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleMarkAsRead(notification.id)}
                        title="Marcar como lida"
                        data-testid={`mark-read-${notification.id}`}
                      >
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(notification.id)}
                      title="Excluir"
                      className="text-slate-400 hover:text-red-500"
                      data-testid={`delete-notification-${notification.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationsPage;
